import React, { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router'
import { clickedOnSearchResult } from '@components/Analytics/performanceAnalytics'
import { Avatar } from '@components/Avatar/Avatar'
import { DirectMessageName } from '@components/DirectMessages/DirectMessageListItem'
import { GroupDMIcon } from '@components/DirectMessages/GroupDMIcon'
import { ZChannelMessageEvent } from '@components/MessageTimeline/util/getEventsByDate'
import { IsolatedMessageItem } from '@components/ResultItem/IsolatedMessageItem'
import { ThreadStatsMap } from '@towns-protocol/sdk'
import { Box, BoxProps, Icon, Paragraph } from '@ui'
import { useCreateLink } from 'hooks/useCreateLink'
import { useDevice } from 'hooks/useDevice'
import {
    TouchChannelResultRow,
    TouchSpaceResultRow,
    TouchUserResultRow,
} from 'touch/HomeTouch/ChannelSection'
import { Channel, checkNever, DmAndGdmModel, useMyProfile } from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

import { SearchMessagesResultItem } from './SearchMessageResultItem'
import { CombinedResult } from './types'

export const ResultItem = (
    props: {
        result: CombinedResult
        misc: {
            channels: Channel[]
            dmChannelIds: DmAndGdmModel[]
            threadsStats: ThreadStatsMap
            spaceId: string | undefined
        }
    } & BoxProps,
) => {
    const { result, selected, misc: miscProps, ...boxProps } = props
    const item = result.item

    const { createLink } = useCreateLink()

    const { dmChannelIds } = miscProps

    const { createDirectMessage } = useCreateUserDM()

    const { isTouch } = useDevice()
    if (selected) {
        boxProps.background = 'level3'
    }

    switch (item.type) {
        case 'user': {
            const userId = item.source.userId

            return isTouch ? (
                <TouchUserResultRow
                    member={item.source}
                    onClick={() => clickedOnSearchResult({ resultType: 'member' })}
                />
            ) : (
                <ItemContainer
                    {...boxProps}
                    onClick={() => {
                        clickedOnSearchResult({ resultType: 'member' })
                        void createDirectMessage(userId, dmChannelIds)
                    }}
                >
                    <Avatar lazy userId={item.source.userId} size="avatar_x4" />
                    <Box centerContent>
                        <Paragraph>{getPrettyDisplayName(item.source)}</Paragraph>
                    </Box>
                </ItemContainer>
            )
        }

        case 'channel': {
            const link = createLink({
                spaceId: miscProps.spaceId,
                channelId: item.source.id,
            })

            return isTouch ? (
                <TouchChannelResultRow
                    itemLink={{ channelId: item.source.id }}
                    name={item.source.label}
                    unread={item.source.unread}
                    mentionCount={item.source.mentionCount}
                    muted={item.source.muted}
                    onClick={() => clickedOnSearchResult({ resultType: 'channel' })}
                />
            ) : link ? (
                <Link to={link} onClick={() => clickedOnSearchResult({ resultType: 'channel' })}>
                    <ItemContainer {...boxProps}>
                        <Box
                            centerContent
                            padding="sm"
                            aspectRatio="square"
                            background="level3"
                            borderRadius="full"
                        >
                            <Icon type="tag" size="square_xs" />
                        </Box>
                        <Box centerContent>
                            <Paragraph>{item.source.label}</Paragraph>
                        </Box>
                    </ItemContainer>
                </Link>
            ) : null
        }

        case 'dmChannel': {
            const link = createLink({
                spaceId: miscProps.spaceId,
                messageId: item.source.id,
            })

            return isTouch ? (
                <TouchChannelResultRow
                    itemLink={{ channelId: item.source.id }}
                    name={
                        <DirectMessageName channelId={item.source.id} label={item.source.label} />
                    }
                    unread={false}
                    mentionCount={0}
                    muted={false}
                    onClick={() => clickedOnSearchResult({ resultType: 'dm channel' })}
                />
            ) : link ? (
                <Link to={link} onClick={() => clickedOnSearchResult({ resultType: 'dm channel' })}>
                    <ItemContainer {...boxProps}>
                        <Box centerContent width="x4">
                            <GroupDMIcon roomIdentifier={item.source.id} width="x4" />
                        </Box>
                        <Box centerContent>
                            <Paragraph>
                                <DirectMessageName
                                    channelId={item.source.id}
                                    label={item.source.label}
                                />
                            </Paragraph>
                        </Box>
                    </ItemContainer>
                </Link>
            ) : null
        }

        case 'dmMessage': {
            return (
                <ItemContainer
                    paddingY="md"
                    {...boxProps}
                    onClick={() => clickedOnSearchResult({ resultType: 'direct message' })}
                >
                    <MessageResultItem
                        event={item.source}
                        channelId={item.channelId}
                        misc={miscProps}
                        highlightedTerms={result.searchResult.terms}
                    />
                </ItemContainer>
            )
        }
        case 'message': {
            return (
                <ItemContainer
                    paddingY="md"
                    {...boxProps}
                    onClick={() => clickedOnSearchResult({ resultType: 'channel message' })}
                >
                    <MessageResultItem
                        event={item.source}
                        channelId={item.channelId}
                        misc={miscProps}
                        highlightedTerms={result.searchResult.terms}
                    />
                </ItemContainer>
            )
        }
        case 'action': {
            return (
                <ItemContainer
                    paddingY="md"
                    {...boxProps}
                    onClick={() => {
                        clickedOnSearchResult({ resultType: 'action' })
                        item.source.callback()
                    }}
                >
                    <Box
                        centerContent
                        padding="sm"
                        aspectRatio="square"
                        background="level3"
                        borderRadius="full"
                    >
                        <Icon type={item.source.icon} size="square_xs" />
                    </Box>
                    <Box centerContent>
                        <Paragraph>{item.source.label}</Paragraph>
                    </Box>
                </ItemContainer>
            )
        }
        case 'space': {
            const link = createLink({ spaceId: item.source.streamId, route: 'townHome' })

            return isTouch ? (
                <TouchSpaceResultRow
                    itemLink={{ spaceId: item.source.streamId }}
                    name={item.source.name}
                    onClick={() => clickedOnSearchResult({ resultType: 'space' })}
                />
            ) : link ? (
                <Link to={link} onClick={() => clickedOnSearchResult({ resultType: 'channel' })}>
                    <ItemContainer
                        {...boxProps}
                        onClick={() => {
                            clickedOnSearchResult({ resultType: 'action' })
                        }}
                    >
                        <Paragraph>{item.source.name}</Paragraph>
                    </ItemContainer>
                </Link>
            ) : null
        }
        default: {
            checkNever(item)
            return null
        }
    }
}

const ItemContainer = (props: BoxProps) => (
    <Box
        hoverable
        shrink
        horizontal
        background="level2"
        gap="sm"
        paddingX="md"
        paddingY="sm"
        {...props}
    />
)

const MessageResultItem = (props: {
    event: ZChannelMessageEvent
    highlightedTerms?: string[]
    channelId: string
    misc: { channels: Channel[]; threadsStats: ThreadStatsMap }
    isolatedLayout?: boolean
}) => {
    const { event, misc, isolatedLayout } = props
    const { isTouch } = useDevice()
    const userId = useMyProfile()?.userId

    const { threadsStats, channels } = misc

    const channel = channels.find((c) => c.id === props.channelId)

    const threadStat =
        channel && event.threadParentId
            ? threadsStats[channel.id]?.[event.threadParentId]
            : undefined

    const result = useMemo(
        () =>
            channel
                ? {
                      type: 'mention' as const,
                      unread: false,
                      event,
                      channelId: channel.id,
                      timestamp: event.createdAtEpochMs,
                      thread: threadStat?.parentEvent,
                  }
                : undefined,
        [channel, event, threadStat?.parentEvent],
    )

    return !result ? undefined : isolatedLayout && isTouch && result ? (
        <Box inset="sm">
            <IsolatedMessageItem result={result} />
        </Box>
    ) : (
        <SearchMessagesResultItem
            highligtTerms={props.highlightedTerms}
            result={result}
            userId={userId}
        />
    )
}

const useCreateUserDM = () => {
    const navigate = useNavigate()
    const { createLink } = useCreateLink()
    const createDirectMessage = useCallback(
        async (userId: string, dmChannelIds: DmAndGdmModel[]) => {
            const link = createLink({ messageId: 'new' })

            if (link && userId) {
                navigate(`${link}?to=${userId}`)
            }
        },
        [createLink, navigate],
    )

    return { createDirectMessage }
}
